import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Mail, CheckCircle, RefreshCw, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function EmailVerification() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    // Send verification email on mount
    if (user?.email) {
      handleSendCode();
    }
  }, []);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleSendCode = async () => {
    if (!user?.email) return;
    
    setResending(true);
    try {
      await axios.post(`${API_URL}/api/email/send-verification`, {
        email: user.email
      });
      toast.success('Verification code sent to your email');
      setCountdown(60);
    } catch (error) {
      if (error.response?.data?.message === 'Email already verified') {
        navigate('/dashboard');
      } else {
        toast.error('Failed to send verification code');
      }
    } finally {
      setResending(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    
    if (code.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/email/verify-code`, {
        email: user.email,
        code: code
      });
      toast.success('Email verified successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sky-100 via-white to-teal-50 p-4" data-testid="email-verification-page">
      <div className="glass-card p-8 max-w-md w-full text-center">
        <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <Mail className="w-10 h-10 text-teal-600" />
        </div>
        
        <h1 className="text-2xl font-bold text-slate-900 mb-2">Verify Your Email</h1>
        <p className="text-slate-600 mb-6">
          We've sent a 6-digit verification code to<br />
          <span className="font-medium text-slate-800">{user?.email}</span>
        </p>
        
        <form onSubmit={handleVerify} className="space-y-4">
          <div>
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="Enter 6-digit code"
              className="text-center text-2xl tracking-widest font-mono"
              maxLength={6}
              data-testid="verification-code-input"
            />
          </div>
          
          <Button
            type="submit"
            disabled={loading || code.length !== 6}
            className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full py-6"
            data-testid="verify-button"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Verifying...
              </>
            ) : (
              <>
                <CheckCircle className="w-5 h-5 mr-2" /> Verify Email
              </>
            )}
          </Button>
        </form>
        
        <div className="mt-6">
          <p className="text-sm text-slate-600 mb-2">Didn't receive the code?</p>
          <Button
            onClick={handleSendCode}
            disabled={resending || countdown > 0}
            variant="outline"
            className="rounded-full"
            data-testid="resend-button"
          >
            {resending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...
              </>
            ) : countdown > 0 ? (
              `Resend in ${countdown}s`
            ) : (
              <>
                <RefreshCw className="w-4 h-4 mr-2" /> Resend Code
              </>
            )}
          </Button>
        </div>
        
        <div className="mt-8 pt-6 border-t border-slate-200">
          <Button
            onClick={handleLogout}
            variant="ghost"
            className="text-slate-500"
          >
            Use a different account
          </Button>
        </div>
      </div>
    </div>
  );
}
